import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Briefcase, ExternalLink, Image, Trophy, FileText, 
  Lightbulb, ChevronLeft, ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PortfolioItem } from '@/types/jobWizard';
import { motion, AnimatePresence } from 'framer-motion';

interface JobPortfolioGalleryProps {
  items: PortfolioItem[];
}

const PORTFOLIO_TYPE_CONFIG: Record<PortfolioItem['type'], { 
  label: string; 
  icon: React.ElementType; 
  color: string;
}> = {
  project: { label: 'Project', icon: Briefcase, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  achievement: { label: 'Achievement', icon: Trophy, color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  publication: { label: 'Publication', icon: FileText, color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  other: { label: 'Other', icon: Lightbulb, color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

const JobPortfolioGallery: React.FC<JobPortfolioGalleryProps> = ({ items }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  // Normalize items to handle both full PortfolioItem objects and potential legacy string[] formats
  const normalizedItems: PortfolioItem[] = (items || []).map((item, idx) => {
    if (typeof item === 'string') {
      return { id: `legacy-${idx}`, title: 'Portfolio Item', imageUrl: item, type: 'other' as const };
    }
    return {
      ...item,
      id: item.id || `item-${idx}`,
      type: item.type || 'other',
    };
  });

  if (normalizedItems.length === 0) return null;

  const itemsWithImages = normalizedItems.filter(item => item.imageUrl);
  const itemsWithoutImages = normalizedItems.filter(item => !item.imageUrl);

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % itemsWithImages.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + itemsWithImages.length) % itemsWithImages.length);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary" />
          Portfolio & Achievements
          <Badge variant="secondary" className="ml-auto text-xs">
            {items.length} {items.length === 1 ? 'Item' : 'Items'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Featured Image Gallery */}
        {itemsWithImages.length > 0 && (
          <div className="relative rounded-xl overflow-hidden bg-muted">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="relative aspect-video"
              >
                <img 
                  src={itemsWithImages[activeIndex].imageUrl} 
                  alt={itemsWithImages[activeIndex].title}
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                
                {/* Overlay with Info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Badge className={PORTFOLIO_TYPE_CONFIG[itemsWithImages[activeIndex].type].color}>
                          {PORTFOLIO_TYPE_CONFIG[itemsWithImages[activeIndex].type].label}
                        </Badge>
                        <h4 className="font-semibold text-white mt-2 truncate">
                          {itemsWithImages[activeIndex].title}
                        </h4>
                        {itemsWithImages[activeIndex].description && (
                          <p className="text-sm text-white/80 mt-1 line-clamp-2">
                            {itemsWithImages[activeIndex].description}
                          </p>
                        )}
                      </div>
                      {itemsWithImages[activeIndex].url && (
                        <Button 
                          size="sm" 
                          variant="secondary" 
                          className="flex-shrink-0"
                          asChild
                        >
                          <a 
                            href={itemsWithImages[activeIndex].url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            {itemsWithImages.length > 1 && (
              <>
                <button
                  onClick={prevSlide}
                  aria-label="Previous portfolio item"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextSlide}
                  aria-label="Next portfolio item"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Dots */}
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {itemsWithImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveIndex(i)}
                      aria-label={`Go to portfolio item ${i + 1}`}
                      className={cn(
                        "w-2 h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        i === activeIndex 
                          ? "bg-white w-4" 
                          : "bg-white/50 hover:bg-white/70"
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* List Items (without images) */}
        {itemsWithoutImages.length > 0 && (
          <div className="grid gap-2">
            {itemsWithoutImages.map((item, index) => {
              const config = PORTFOLIO_TYPE_CONFIG[item.type];
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                >
                  <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", config.color)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{item.title}</h4>
                    {item.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {item.description}
                      </p>
                    )}
                  </div>
                  {item.url && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      asChild
                    >
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobPortfolioGallery;
